/**
 * Compute 3x3 projective (homography) matrix from 4 point correspondences
 * and convert to CSS matrix3d string.
 * @param {Array<{x: number, y: number}>} originalPos - 4 corners of source rectangle
 * @param {Array<{x: number, y: number}>} currentPos - 4 corners of destination quad
 * @returns {string} CSS matrix3d() value
 */
export function computeProjectiveMatrix3d(originalPos, currentPos) {
  const origin = originalPos[0];
  const src = originalPos.map((p) => ({ x: p.x - origin.x, y: p.y - origin.y }));
  const dst = currentPos.map((p) => ({ x: p.x - origin.x, y: p.y - origin.y }));

  const A = [];
  const b = [];

  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const u = dst[i].x;
    const v = dst[i].y;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(u);
    b.push(v);
  }

  const h = solveLinearSystem(A, b);
  const H3 = [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], 1]
  ];

  const H4 = [
    [H3[0][0], H3[0][1], 0, H3[0][2]],
    [H3[1][0], H3[1][1], 0, H3[1][2]],
    [0, 0, 1, 0],
    [H3[2][0], H3[2][1], 0, 1]
  ];

  const m = [];
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) m.push(H4[row][col]);
  }
  return `matrix3d(${m.join(',')})`;
}

function solveLinearSystem(A, b) {
  const n = b.length;
  const augmented = A.map((row, i) => [...row, b[i]]);

  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) maxRow = k;
    }
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    for (let k = i + 1; k < n; k++) {
      const c = augmented[k][i] / augmented[i][i];
      for (let j = i; j <= n; j++) {
        augmented[k][j] = j === i ? 0 : augmented[k][j] - c * augmented[i][j];
      }
    }
  }

  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = augmented[i][n];
    for (let j = i + 1; j < n; j++) x[i] -= augmented[i][j] * x[j];
    x[i] /= augmented[i][i];
  }
  return x;
}
